import type { StateCreator } from 'zustand';
import type { StoreState } from './index';
import type { AutopilotConfig, AutopilotNotification, AutopilotStatus, SkipLogEntry } from '../types';
import { autopilotApi } from '../services/autopilotApi';

export interface AutopilotSlice {
  autopilotConfig: AutopilotConfig | null;
  autopilotStatus: AutopilotStatus | null;
  autopilotNotifications: AutopilotNotification[];
  autopilotSkipLog: SkipLogEntry[];
  autopilotLoading: boolean;

  fetchAutopilotConfig: (channelId: number) => Promise<void>;
  updateAutopilotConfig: (channelId: number, updates: Partial<AutopilotConfig>) => Promise<void>;
  enableAutopilot: (channelId: number) => Promise<{ success: boolean; missing?: string[] }>;
  pauseAutopilot: (channelId: number) => Promise<void>;
  resumeAutopilot: (channelId: number) => Promise<void>;
  fetchAutopilotStatus: (channelId: number) => Promise<void>;
  fetchAutopilotNotifications: (channelId: number) => Promise<void>;
  markAllAutopilotRead: (channelId: number) => Promise<void>;
  fetchSkipLog: (channelId: number) => Promise<void>;
  handleAutopilotSSE: (event: string, data: any) => void;
}

export const createAutopilotSlice: StateCreator<StoreState, [], [], AutopilotSlice> = (set, _get) => ({
  autopilotConfig: null,
  autopilotStatus: null,
  autopilotNotifications: [],
  autopilotSkipLog: [],
  autopilotLoading: false,

  fetchAutopilotConfig: async (channelId) => {
    set({ autopilotLoading: true });
    try {
      const cfg = await autopilotApi.getConfig(channelId);
      set({ autopilotConfig: cfg, autopilotLoading: false });
    } catch {
      set({ autopilotLoading: false });
    }
  },

  updateAutopilotConfig: async (channelId, updates) => {
    try {
      const cfg = await autopilotApi.updateConfig(channelId, updates);
      set({ autopilotConfig: cfg });
    } catch {
      // ignore
    }
  },

  enableAutopilot: async (channelId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/autopilot/${channelId}/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.status === 422) {
        const body = await res.json();
        return { success: false, missing: body.detail.missing as string[] };
      }
      if (!res.ok) {
        return { success: false, missing: [] };
      }
      const cfg = await res.json() as AutopilotConfig;
      set({ autopilotConfig: cfg });
      return { success: true };
    } catch {
      return { success: false, missing: [] };
    }
  },

  pauseAutopilot: async (channelId) => {
    try {
      await autopilotApi.pause(channelId);
      set((s) => ({
        autopilotConfig: s.autopilotConfig ? { ...s.autopilotConfig, paused: true } : null,
      }));
    } catch {
      // ignore
    }
  },

  resumeAutopilot: async (channelId) => {
    try {
      await autopilotApi.resume(channelId);
      set((s) => ({
        autopilotConfig: s.autopilotConfig ? { ...s.autopilotConfig, paused: false } : null,
      }));
    } catch {
      // ignore
    }
  },

  fetchAutopilotStatus: async (channelId) => {
    try {
      const status = await autopilotApi.getStatus(channelId);
      set({ autopilotStatus: status });
    } catch {
      // ignore
    }
  },

  fetchAutopilotNotifications: async (channelId) => {
    try {
      const notifications = await autopilotApi.getNotifications(channelId);
      set({ autopilotNotifications: notifications });
    } catch {
      // ignore
    }
  },

  markAllAutopilotRead: async (channelId) => {
    try {
      await autopilotApi.markAllRead(channelId);
      set((s) => ({
        autopilotNotifications: s.autopilotNotifications.map((n) => ({ ...n, read: true })),
      }));
    } catch {
      // ignore
    }
  },

  fetchSkipLog: async (channelId) => {
    try {
      const log = await autopilotApi.getSkipLog(channelId);
      set({ autopilotSkipLog: log });
    } catch {
      // ignore
    }
  },

  handleAutopilotSSE: (event, data) => {
    switch (event) {
      case 'autopilot_notification':
        set((s) => ({
          autopilotNotifications: [data as AutopilotNotification, ...s.autopilotNotifications],
        }));
        break;
      case 'autopilot_skip':
        set((s) => ({
          autopilotSkipLog: [data as SkipLogEntry, ...s.autopilotSkipLog],
        }));
        break;
      case 'autopilot_phase_upgrade':
        set((s) => ({
          autopilotConfig: s.autopilotConfig
            ? { ...s.autopilotConfig, warmup_phase: data.to_phase }
            : null,
        }));
        break;
      case 'autopilot_paused':
        set((s) => ({
          autopilotConfig: s.autopilotConfig ? { ...s.autopilotConfig, paused: true } : null,
        }));
        break;
      case 'autopilot_resumed':
        set((s) => ({
          autopilotConfig: s.autopilotConfig ? { ...s.autopilotConfig, paused: false } : null,
        }));
        break;
      case 'autopilot_scheduled':
        set((s) => ({
          autopilotStatus: s.autopilotStatus
            ? { ...s.autopilotStatus, scheduled_slots_count: s.autopilotStatus.scheduled_slots_count + 1 }
            : null,
        }));
        break;
      case 'autopilot_queue_low':
        set((s) => ({
          autopilotStatus: s.autopilotStatus
            ? { ...s.autopilotStatus, queue_depth: (data as any).queue_depth }
            : null,
        }));
        break;
    }
  },
});
